import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { Pill } from "@/components/brand/Pill";
import type { DiagnosticTabProps } from "@/components/projects/diagnostic/DiagnosticSection";
import {
  gapCategories,
  gapRows,
  type GapRow,
} from "@/components/projects/diagnostic/diagnosticData";
import {
  gapReasonLabel,
  makeCoverageTypeMeta,
  type CoverageMeta,
} from "@/components/projects/diagnostic/diagnosticMeta";
import { NO_VALUE } from "@/components/projects/diagnostic/sectionState";
import { formatCount } from "@/lib/format";

const HEAD = "px-2 py-2 font-mono text-[9px] font-medium uppercase tracking-[0.08em] text-[#7b8799]";
const CELL = "px-2 py-2 align-top";

/** O portal de notas da SAP busca pelo número sem os zeros do formato interno. */
function noteUrl(note: string): string {
  return `https://me.sap.com/notes/${note.replace(/^0+/, "")}`;
}

function NoteLink({ note }: { note: string }) {
  const number = note.replace(/^0+/, "") || note;
  return (
    <a
      href={noteUrl(note)}
      target="_blank"
      rel="noreferrer"
      className="font-mono text-[#08254c] underline decoration-[#c4cfdd] underline-offset-2 hover:decoration-[#08254c]"
    >
      {number}
    </a>
  );
}

/**
 * Os gaps do Ato 2, um por linha (#1479).
 *
 * Aberto pelo card "Gaps identificados por categoria", sem filtro ou já filtrado
 * pela categoria clicada. O card conta por categoria e não lista, e a lista mora
 * aqui, atrás da rota: é o que o consultor abre quando o cliente pergunta "por que
 * esse gap?". Sem valor financeiro, como o card: preço é do Ato 5.
 *
 * A lista é a amostra que o builder entrega (capada em 100), e o filtro conta pela
 * população inteira (`by_coverage_type`). Quando as duas não fecham, a tela diz.
 */
export function ScopeGapsDetail({ sections }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const meta = makeCoverageTypeMeta(t) as Record<string, CoverageMeta | undefined>;
  const label = (coverageType: string | undefined) => {
    if (!coverageType || coverageType === "_UNKNOWN") return t("Coverage not recognized");
    return meta[coverageType]?.label ?? coverageType;
  };

  const rows = gapRows(sections);
  const { rows: categories, total } = gapCategories(sections);
  const selected = params.get("category");
  const shown = selected ? rows.filter((row) => (row.coverage_type ?? "_UNKNOWN") === selected) : rows;

  const choose = (category: string | null) => {
    const next = new URLSearchParams(params);
    if (category) next.set("category", category);
    else next.delete("category");
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <p className="max-w-3xl text-[13px] leading-relaxed text-[#596b85]">
        {t(
          "Each scope item that the standard does not cover as it is, with the reason and the SAP note behind it. No value here: price belongs to Investment.",
        )}
      </p>

      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2" role="group" aria-label={t("Category")}>
          <FilterChip
            testId="scope-gap-filter-all"
            pressed={!selected}
            onClick={() => choose(null)}
            text={t("All")}
            count={total}
          />
          {categories.map((category) => (
            <FilterChip
              key={category.coverageType}
              testId={`scope-gap-filter-${category.coverageType}`}
              pressed={selected === category.coverageType}
              onClick={() => choose(category.coverageType)}
              text={label(category.coverageType)}
              count={category.count}
            />
          ))}
        </div>
      )}

      {total !== undefined && total > rows.length && (
        <p data-testid="scope-gaps-sampling" className="text-[11px] text-[#596b85]">
          {t("Showing {{shown}} of {{total}}", {
            shown: formatCount(rows.length),
            total: formatCount(total),
          })}
        </p>
      )}

      {shown.length === 0 ? (
        <p className="rounded-md border border-[#e8ebf0] bg-[#f7f9fc] px-4 py-3 text-[13px] text-[#596b85]">
          {t("No gap in this category.")}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-[#e8ebf0] bg-white">
          <table className="w-full min-w-[760px] border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-[#e8ebf0] bg-[#f7f9fc] text-left">
                <th className={HEAD}>{t("Scope item")}</th>
                <th className={HEAD}>{t("Name")}</th>
                <th className={HEAD}>{t("LoB")}</th>
                <th className={HEAD}>{t("Category")}</th>
                <th className={HEAD}>{t("Reason")}</th>
                <th className={HEAD}>{t("SAP note")}</th>
                <th className={HEAD}>{t("Scope")}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <GapLine
                  key={row.scope_item_id ?? row.scope_item_name}
                  row={row}
                  category={label(row.coverage_type)}
                  tone={row.coverage_type ? meta[row.coverage_type]?.tone : undefined}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}

function FilterChip({
  testId,
  pressed,
  onClick,
  text,
  count,
}: {
  testId: string;
  pressed: boolean;
  onClick: () => void;
  text: string;
  count: number | undefined;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={pressed}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-semibold ${
        pressed
          ? "border-[#08254c] bg-[#08254c] text-white"
          : "border-[#d7dde6] bg-white text-[#08254c] hover:bg-[#f5f8fc]"
      }`}
    >
      {text}
      {count !== undefined && <span className="font-mono tabular-nums">{formatCount(count)}</span>}
    </button>
  );
}

function GapLine({
  row,
  category,
  tone,
}: {
  row: GapRow;
  category: string;
  tone: CoverageMeta["tone"] | undefined;
}) {
  const { t } = useTranslation();
  return (
    <tr
      data-testid={`scope-gap-row-${row.scope_item_id ?? "unknown"}`}
      className="border-b border-[#eef1f5] last:border-b-0"
    >
      <td className={`${CELL} font-mono text-[#08254c]`}>{row.scope_item_id ?? NO_VALUE}</td>
      <td className={`${CELL} text-[#08254c]`}>{row.scope_item_name ?? NO_VALUE}</td>
      <td className={`${CELL} text-[#596b85]`}>{row.lob ?? NO_VALUE}</td>
      <td className={CELL}>{tone ? <Pill tone={tone}>{category}</Pill> : category}</td>
      <td className={`${CELL} text-[#596b85]`}>
        {row.gap_reasons.length > 0
          ? row.gap_reasons.map((reason) => gapReasonLabel(reason, t)).join(" · ")
          : NO_VALUE}
      </td>
      <td className={CELL}>
        {row.sap_notes.length > 0 ? (
          <span className="flex flex-wrap gap-x-2">
            {row.sap_notes.map((note) => (
              <NoteLink key={note} note={note} />
            ))}
          </span>
        ) : (
          NO_VALUE
        )}
      </td>
      <td className={`${CELL} text-[#596b85]`}>
        {row.out_of_scope === undefined ? NO_VALUE : row.out_of_scope ? t("Out of scope") : t("In scope")}
      </td>
    </tr>
  );
}
